import { ApplicationInsights } from '@microsoft/applicationinsights-web';

const CONNECTION_STRING = 'InstrumentationKey=6dbb269f-7d9e-4371-b13b-a5467603ed5a;IngestionEndpoint=https://westeurope-5.in.applicationinsights.azure.com/;LiveEndpoint=https://westeurope.livediagnostics.monitor.azure.com/;ApplicationId=4ccb532b-590b-4634-a131-468e29dd3343';
const buildVersion = typeof __APP_INSIGHTS_BUILD_VERSION__ !== 'undefined'
  ? __APP_INSIGHTS_BUILD_VERSION__
  : 'dev';
const localHosts = ['localhost', '127.0.0.1', '::1'];
const isLocalHost = localHosts.includes(window.location.hostname)
  || window.location.hostname.endsWith('.lan');
const ignoredErrorMessages = [
  'Script error.',
  '$ is not defined',
  'jQuery is not defined',
  'can\'t access property "fn", t is undefined',
  'ResizeObserver loop',
  'Blocked a frame',
  'chrome-extension',
  'PULL_DISABLED',
  'Push & Pull server is disabled',
];

function containsIgnoredSource(value) {
  if (!value || typeof value !== 'string') {
    return false;
  }

  return value.includes('chrome-extension://')
    || value.includes('snap.licdn.com/li.lms-analytics');
}

function shouldIgnoreFailedFetchTelemetry({ message, stack, reasonValue }) {
  if (message !== 'TypeError: Failed to fetch' && message !== 'Failed to fetch') {
    return false;
  }

  return containsIgnoredSource(stack) || containsIgnoredSource(reasonValue);
}

function isLandingAppendChildNullError(value) {
  if (!value || typeof value !== 'string') {
    return false;
  }

  return value.includes("Cannot read properties of null (reading 'appendChild')")
    || (
      value.includes('null is not an object')
      && value.includes('appendChild')
    );
}

function shouldIgnoreAndroidNativeBridgeTelemetry(...values) {
  const texts = values.filter(
    (value) => typeof value === 'string'
  );

  const hasMessage = texts.some((value) =>
    value.includes('Error invoking postMessage: Java object is gone')
  );

  const hasSource = texts.some((value) =>
    value.includes('iabjs://navigation_performance_logger_android')
  );

  return hasMessage && hasSource;
}

function shouldIgnoreLandingStarsTelemetry({ message, stack, reasonValue }) {
  const isAppendChildError = isLandingAppendChildNullError(message)
    || isLandingAppendChildNullError(reasonValue);

  if (!isAppendChildError) {
    return false;
  }

  const source = `${stack}\n${reasonValue}`;

  return source.includes('/upload/landing/assets/assets_webpack_')
    && source.includes('appendChild');
}

function stringifyUnhandledReason(reason) {
  if (typeof reason === 'string') {
    return reason;
  }

  if (reason instanceof Error) {
    return reason.stack || reason.message || String(reason);
  }

  try {
    const serialized = JSON.stringify(reason);

    return typeof serialized === 'string'
      ? serialized
      : String(reason);
  } catch {
    return String(reason);
  }
}

function getUnhandledReasonType(reason) {
  if (reason === null) {
    return 'null';
  }

  if (reason instanceof Error) {
    return reason.name || 'Error';
  }

  if (typeof reason === 'object' && reason && typeof reason.name === 'string') {
    return reason.name;
  }

  return typeof reason;
}

function createUnhandledRejectionError(reason) {
  if (reason instanceof Error) {
    return reason;
  }

  const fallbackMessage = 'Unhandled promise rejection';
  const message = typeof reason === 'string'
    ? reason
    : reason && typeof reason.message === 'string'
      ? reason.message
      : fallbackMessage;
  const error = new Error(message);

  if (reason && typeof reason === 'object') {
    if (typeof reason.name === 'string') {
      error.name = reason.name;
    }

    if (typeof reason.stack === 'string') {
      error.stack = reason.stack;
    }
  }

  return error;
}

function hasUnhandledRejectionStack(reason) {
  return !!(reason && typeof reason === 'object' && typeof reason.stack === 'string');
}

function containsRecaptchaSource(value) {
  return typeof value === 'string' && value.includes('https://www.gstatic.com/recaptcha/');
}

function shouldIgnoreRecaptchaTelemetry({message = '', stack = '', reasonValue = '', url = '',}) {
  return [
    message,
    stack,
    reasonValue,
    url,
  ].some(containsRecaptchaSource);
}

function shouldIgnoreObjectNotFoundTelemetry(...values) {
  const pattern =
    /\bObject Not Found Matching Id:\s*\d+\s*,\s*MethodName:\s*update\s*,\s*ParamCount:\s*4\b/;

  return values.some(
    (value) => typeof value === 'string' && pattern.test(value)
  );
}

function collectErrorTexts(item) {
  const data = item.baseData || {};
  const properties = data.properties || {};
  const customData = item.data || {};
  const exceptions = Array.isArray(data.exceptions)
    ? data.exceptions
    : [];

  const values = [
    data.message,
    properties.message,
    properties.reasonValue,
    properties.stack,
    customData.message,
    customData.reasonValue,
    customData.stack,
  ];

  for (const exception of exceptions) {
    if (!exception) continue;

    values.push(
      exception.message,
      exception.stack,
      exception.rawStack
    );

    if (Array.isArray(exception.parsedStack)) {
      for (const frame of exception.parsedStack) {
        if (frame) {
          values.push(frame.assembly, frame.fileName);
        }
      }
    }
  }

  return values.filter((value) => typeof value === 'string');
}

if (!isLocalHost && !window.__APP_INSIGHTS_INITIALIZED__) {
  window.__APP_INSIGHTS_INITIALIZED__ = true;

  const appInsights = new ApplicationInsights({
    config: {
      connectionString: CONNECTION_STRING,
      enableAutoRouteTracking: false,
      disableAjaxTracking: false,
      disableFetchTracking: false,
      enableUnhandledPromiseRejectionTracking: false,
      enableCorsCorrelation: true,
      correlationHeaderDomains: [
        '1ci.com',
        '*.1ci.com',
        '1ci.ro',
        '*.1ci.ro',
        '1ci.pl',
        '*.1ci.pl',
        '1ci.com.tr',
        '*.1ci.com.tr',
        '1ci.it',
        '*.1ci.it',
        '1cinternational.com',
        '*.1cinternational.com',
      ],
      correlationHeaderExcludedDomains: ['mc.yandex.ru', 'px.ads.linkedin.com'],
      enableRequestHeaderTracking: false,
      enableResponseHeaderTracking: false,
    },
  });

  appInsights.loadAppInsights();
  window.appInsights = appInsights;

  appInsights.addTelemetryInitializer((item) => {
    try {
      item.tags = item.tags || {};
      item.tags['ai.operation.name'] = window.location.pathname;
      item.tags['ai.cloud.role'] =
        window.location.hostname === 'blog.1ci.com'
          ? '1ci-blog'
          : '1ci-site';
      item.tags['ai.cloud.roleInstance'] = window.location.hostname;
      item.tags['ai.application.ver'] = buildVersion;
      return true;
    } catch (e) {
      return true;
    }
  });

  appInsights.addTelemetryInitializer((item) => {
    try {
      const data = item.baseData || {};
      const properties = data.properties || {};

      if (
        item.baseType === 'ExceptionData' ||
        item.baseType === 'MessageData'
      ) {
        const texts = collectErrorTexts(item);

        if (
          shouldIgnoreObjectNotFoundTelemetry(...texts) ||
          shouldIgnoreAndroidNativeBridgeTelemetry(...texts)
        ) {
          return false;
        }
      }

      if (item.baseType !== 'ExceptionData') {
        return true;
      }

      const exception = Array.isArray(data.exceptions)
        ? data.exceptions[0]
        : undefined;
      const message = exception && exception.message ? exception.message : '';
      const stack = exception && exception.stack ? exception.stack : '';
      const reasonValue = properties.reasonValue || '';
      const url = properties.url || '';

      /*
        отфильтрованы все ошибки, где URL reCAPTCHA присутствует в:
        - message;
        - stack;
        - reasonValue;
        - properties.url.
       */

      if (shouldIgnoreRecaptchaTelemetry({message, stack, reasonValue, url,})) {
        return false;
      }

      if (ignoredErrorMessages.some((fragment) => message.includes(fragment))) {
        return false;
      }

      if (shouldIgnoreFailedFetchTelemetry({ message, stack, reasonValue })) {
        return false;
      }

      if (shouldIgnoreLandingStarsTelemetry({ message, stack, reasonValue })) {
        return false;
      }

      if (exception && exception.message) {
        exception.message = exception.message
          .replace(/0x[a-fA-F0-9]+/g, '{hex}')
          .replace(/\d+/g, '{n}');
      }
      return true;
    } catch (e) {
      return true;
    }
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const reasonValue = stringifyUnhandledReason(reason);
    const properties = {
      type: 'unhandledrejection',
      url: window.location.href,
      reasonType: getUnhandledReasonType(reason),
      reasonValue,
    };

    const reasonMessage = reason instanceof Error && typeof reason.message === 'string'
      ? reason.message
      : typeof reason === 'string'
        ? reason
        : reason && typeof reason.message === 'string'
          ? reason.message
          : '';
    const reasonStack = reason instanceof Error && typeof reason.stack === 'string'
      ? reason.stack
      : reason && typeof reason === 'object' && typeof reason.stack === 'string'
        ? reason.stack
        : '';

    if (
      shouldIgnoreObjectNotFoundTelemetry(
        reasonMessage,
        reasonStack,
        reasonValue
      ) ||
      shouldIgnoreAndroidNativeBridgeTelemetry(
        reasonMessage,
        reasonStack,
        reasonValue
      )
    ) {
      return;
    }

    if (shouldIgnoreRecaptchaTelemetry({
      message: reasonMessage,
      stack: reasonStack,
      reasonValue,
      url: window.location.href,
    })) {
      return;
    }

    if (shouldIgnoreFailedFetchTelemetry({
      message: reasonMessage,
      stack: reasonStack,
      reasonValue,
    })) {
      return;
    }

    if (shouldIgnoreLandingStarsTelemetry({
      message: reasonMessage,
      stack: reasonStack,
      reasonValue,
    })) {
      return;
    }

    if (reason instanceof Error || hasUnhandledRejectionStack(reason)) {
      appInsights.trackException({
        exception: createUnhandledRejectionError(reason),
        properties,
      });

      return;
    }

    appInsights.trackTrace({
      message: 'Unhandled promise rejection',
      properties,
    });
  });

  (function waitForBX(attempt) {
    const currentAttempt = typeof attempt === 'number' ? attempt : 0;

    if (window.BX && window.BX.message && window.BX.message.USER_ID) {
      appInsights.setAuthenticatedUserContext(String(window.BX.message.USER_ID), undefined, false);
      return;
    }

    if (currentAttempt < 20) {
      window.setTimeout(() => waitForBX(currentAttempt + 1), 500);
    }
  }());

  appInsights.trackPageView();
}
